// Copyright (c) StrangeBee 2024

import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { Tabs } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import { replace } from '@Services/RouteBuilder';
import { useLocalMessage, buildPath, classList, unBuildPath } from '@Helpers/';
import { RouteType } from '@Types';
import { useCollapse } from '@Molecules/THLayout/hook';
import { usePin } from '@Molecules/Pin';
import { TabContext } from '@Context/TabProvider';
import { ICONS } from '@Constants/iconsApp';

import './THNavBar.scss';
import './PagesView.scss';

const { TabPane } = Tabs;

const checkLocation = (location: string, routes: RouteType[], paramName: string, entityId: string): string => {
    return routes.reduce((acc, { path }) => {
        const pathCompute = replace(path, { [paramName]: entityId });
        return location.includes(pathCompute) ? pathCompute : acc;
    }, location);
};

type tabLibProps = {
    tabList: ReactElement[];
    lib: Record<string, boolean>;
};

interface THNavBarProps {
    entityId?: string;
    paramName: string;
    routes: RouteType[];
    extraContent?: ReactElement | ReactElement[];
}

const THNavBar = React.memo(({ entityId = '', paramName, routes, extraContent }: THNavBarProps): ReactElement => {
    const [sider, setSider] = useState(false);

    const i = useLocalMessage();
    const history = useHistory();
    const location = useLocation();

    const { collapsed, setCollapsed, hasSider } = useCollapse();
    const { opened, remove, add } = useContext(TabContext);
    const { shortcutVisible } = usePin();

    const [routesDisplay, setRoutesDisplay] = useState<RouteType[]>(routes);

    const currentPath = useMemo(
        () => checkLocation(location.pathname, routes, paramName, entityId),
        [location.pathname, paramName, entityId, routes],
    );

    const { tabList, lib } = useMemo(
        () =>
            routesDisplay.reduce(
                (tabs, { menu, path }): tabLibProps => {
                    if (!menu) {
                        return tabs;
                    }

                    const labelComp = (
                        <span className="tab-panel__label">
                            {menu?.render || (
                                <>
                                    {menu?.icon}
                                    {menu?.label && i(menu?.label)}
                                </>
                            )}
                        </span>
                    );

                    const pathKey = buildPath(path, paramName, entityId);
                    return {
                        tabList: [
                            ...tabs.tabList,
                            <TabPane tab={labelComp} key={pathKey} closable={menu?.shortcut?.enable || false} />,
                        ],
                        lib: { ...tabs.lib, [buildPath(path, paramName, entityId)]: !!hasSider },
                    };
                },
                { tabList: [], lib: {} } as tabLibProps,
            ),
        [entityId, i, paramName, routesDisplay, hasSider],
    );

    const onTabChange = (key: string): void => {
        if (key !== location.pathname && !shortcutVisible) {
            history.push(key);
            setSider(hasSider);
        }
    };

    const onEditTab = (key: any, action: any): void => {
        if (action == 'remove') {
            const path = unBuildPath(key, paramName, entityId);
            remove(path);
            if (key === currentPath) {
                const firstRoute = routesDisplay[0];
                history.push(buildPath(firstRoute.path, paramName, entityId));
            }
        }
    };

    let tabBarExtraContent = [] as ReactElement[];

    if (extraContent) {
        tabBarExtraContent = Array.isArray(extraContent) ? [...extraContent] : [extraContent];
    }

    useEffect(() => {
        const unclosable = routes.filter(({ menu }) => !menu?.shortcut);
        const opennedRoute = routes.filter(({ path }) => opened.includes(path));
        setRoutesDisplay([...unclosable, ...opennedRoute]);
    }, [routes, opened]);

    useEffect(() => {
        const shortcutList = routes.filter(({ menu }) => menu?.shortcut).map(({ path }) => path);
        const unclosable = routes.filter(({ menu }) => !menu?.shortcut).map(({ path }) => path);
        const opennedRoute = routes.filter(({ path }) => opened.includes(path)).map(({ path }) => path);

        const path = unBuildPath(currentPath, paramName, entityId);
        if (!unclosable.includes(path) && !opennedRoute.includes(path) && shortcutList.includes(path)) {
            add(path);
        }
    }, [add, currentPath, entityId, opened, paramName, routes]);

    useEffect(() => {
        setSider(hasSider);
    }, [entityId, lib, location.pathname, paramName, hasSider]);

    return (
        <div
            className={classList('th-sub-layout_header', 'editable', {
                'sider-state': sider,
            })}
        >
            <Tabs
                hideAdd
                type="editable-card"
                activeKey={currentPath}
                animated={false}
                size="small"
                className="tabs"
                onTabClick={onTabChange}
                tabBarExtraContent={tabBarExtraContent}
                onEdit={onEditTab}
            >
                {tabList}
            </Tabs>
            {sider && (
                <span
                    className={`th-sub-layout-header__collapsed ${collapsed ? '' : 'active'}`}
                    onClick={(): void => setCollapsed(!collapsed)}
                >
                    <ICONS.VerticalAlignBottomOutlined />
                </span>
            )}
        </div>
    );
});

export default THNavBar;
